<template>
  <div class="block" data-testid="kss-recurring-section">
    <div class="flex items-center justify-center gap-2">
      <Checkbox v-model='isRecurringTask' binary size="large" id="rec_box"
        data-testid="kss-recurring-checkbox"/>
      <label for="rec_box" class="flex items-center justify-center cursor-pointer" id="lbl_recBox2">
        {{ $t('KssRecurringTaskComponent.isRecurringTask') }}
      </label>
    </div>
    <div v-if="isRecurringTask" class="w-full py-4 px-4 my-2" id="drop_event" data-testid="kss-recurring-options">
      <div class="flex items-center justify-center">
        <div>
          <Select v-model="type" :options="recTaskDropdown" optionLabel="name"
            optionValue ="value" class="w-56 bg-[var(--color-light)] h-10"
            name="eventtime" id="select_event" data-testid="kss-recurring-type"/>
        </div>
      </div>
      <div v-if="normalizedType === 'custom'" class="flex flex-wrap justify-center items-center flex-row mt-4
        gap-2" id="custom_choice">
        <label class="flex relative m-1 text-base w-fit" for="nmbr">
            {{ $t('KssRecurringTaskComponent.repeatEvery') }}
        </label>
        <InputNumber v-model="everyX" min="1" id="nmbr" inputStyle="width:50px" class="h-10"/>
        <Select v-model="subtype" :options="customDropdown" optionLabel="name"
            optionValue ="value" class="w-32 bg-[var(--color-light)] h-10"
            name="nmbrevent" id="nmbr_event"/>
      </div>
      <div v-if="normalizedType === 'monthly'" class="flex flex-wrap justify-center items-center flex-row mt-4
        gap-2" id="monthly_week_choice">
        <label class="flex relative m-1 text-base w-fit" for="week_of_month">
          {{ $t('KssRecurringTaskComponent.weekOfMonth') }}
        </label>
        <Select
          v-model="weekOfMonth"
          :options="weekOfMonthOptions"
          optionLabel="name"
          optionValue="value"
          class="w-64 bg-[var(--color-light)] h-10"
          inputId="week_of_month"
        />
      </div>
      <div v-if="normalizedType === 'daily' || (normalizedType === 'custom' && subtype === 'days')"
        class="flex items-center justify-center gap-2 mt-4">
        <Checkbox v-model="includeWeekends" binary id="include_weekends"/>
        <label for="include_weekends" class="cursor-pointer text-base">
          {{ $t('KssRecurringTaskComponent.includeWeekends') }}
        </label>
      </div>
      <div class="flex flex-col items-center justify-center gap-2 mt-4">
        <label for="series_end_date" class="text-base">
          {{ $t('KssRecurringTaskComponent.seriesEndDate') }}
        </label>
        <DatePicker
          inputId="series_end_date"
          :modelValue="seriesEndDateValue"
          @update:modelValue="onSeriesEndDateChange"
          :dateFormat="PRIMEVUE_DATE_FORMAT"
                :placeholder="LOCALE_DATE_PLACEHOLDER"
          class="h-10 rounded-lg bg-[var(--color-light)]"
          showIcon
          iconDisplay="input"
        />
        <button
          v-if="seriesEndDateValue"
          type="button"
          class="text-sm text-[var(--color-orange)]"
          @click="clearSeriesEndDate"
        >
          {{ $t('KssRecurringTaskComponent.clearSeriesEndDate') }}
        </button>
      </div>
      <div v-if="showWeekDays" id="seven_days" data-testid="kss-recurring-days"
        class="flex items-center justify-center flex-wrap mt-6">
        <label id="day" v-for="(day, index) in getDays" :key="day.rSchName" @click="selectDay(index)"
            class="flex relative m-1 text-sm w-fit"
            :data-testid="`kss-recurring-day-${day.rSchName}`">
          <div class="flex p-3 w-full rounded-lg m-0 cursor-pointer border-[1px]
                    border-[var(--color-light-grey)]"
        :class="{'text-[var(--color-orange)] border-[var(--color-orange)] bg-[var(--color-bisque)]'
                     : day.isSelected}">
            <span :class="{'font-semibold': day.isSelected}">
              {{ $t('KssRecurringTaskComponent.daysShort.' + day.rSchName) }}</span>
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { Checkbox, InputNumber, Select } from 'primevue';
import DatePicker from 'primevue/datepicker';
import { PRIMEVUE_DATE_FORMAT, LOCALE_DATE_PLACEHOLDER } from '@/helpers/formatLocaleDate';

export default {
  name: 'KssRecurringTaskComponent',
  components: { Checkbox, Select, InputNumber, DatePicker },
  data() {
    return {
      type: this.$store.state.kss.recurringType,
      subtype: this.$store.state.kss.recurringSubtype,
      everyX: this.$store.state.kss.recurringEveryX,
      PRIMEVUE_DATE_FORMAT,
      LOCALE_DATE_PLACEHOLDER,
      recTaskDropdown: [
        { name: this.$t('KssRecurringTaskComponent.daily'), value: 'daily' },
        { name: this.$t('KssRecurringTaskComponent.weekly'), value: 'weekly' },
        { name: this.$t('KssRecurringTaskComponent.monthly'), value: 'monthly' },
        { name: this.$t('KssRecurringTaskComponent.custom'), value: 'custom' },
      ],
      customDropdown: [
        { name: this.$t('KssRecurringTaskComponent.days'), value: 'days' },
        { name: this.$t('KssRecurringTaskComponent.weeks'), value: 'weeks' },
      ],
      weekOfMonthOptions: [
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption1'), value: 1 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption2'), value: 2 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption3'), value: 3 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption4'), value: 4 },
        { name: this.$t('KssRecurringTaskComponent.weekOfMonthOption5'), value: 5 },
      ],
    };
  },
  computed: {
    isRecurringTask: {
      get() {
        return this.$store.getters.getIsRecurringTask;
      },
      set(newValue) {
        this.$store.commit('setIsRecurringTask', newValue);
      },
    },
    getDays() {
      return this.$store.getters.getRecurringDays;
    },
    normalizedType() {
      const t = this.type;
      if (t && typeof t === 'object' && t.value != null) return t.value;
      return t;
    },
    showWeekDays() {
      const t = this.normalizedType;
      return t === 'weekly'
        || t === 'monthly'
        || (t === 'custom' && this.subtype === 'weeks');
    },
    weekOfMonth: {
      get() {
        return this.$store.getters.getRecurringWeekOfMonth;
      },
      set(value) {
        this.$store.commit('setRecurringWeekOfMonth', value);
      },
    },
    includeWeekends: {
      get() {
        return this.$store.getters.getIncludeWeekends;
      },
      set(value) {
        this.$store.commit('setIncludeWeekends', value);
      },
    },
    seriesEndDateValue() {
      return this.$store.getters.getSeriesEndDate;
    },
  },
  methods: {
    onSeriesEndDateChange(value) {
      this.$store.commit('setSeriesEndDate', value || null);
    },
    clearSeriesEndDate() {
      this.$store.commit('setSeriesEndDate', null);
    },
    selectDay(index) {
      if (this.normalizedType === 'monthly') {
        this.$store.commit('selectRecurringDateMonthly', index);
      } else {
        this.$store.commit('selectRecurringDate', index);
      }
    },
  },
  watch: {
    type(value) {
      const next = value && typeof value === 'object' && value.value != null ? value.value : value;
      if (next !== this.type) {
        this.type = next;
        return;
      }
      this.$store.commit('setRecurringType', next);
      this.subtype = 'days';
      this.everyX = 1;
    },
    subtype(value) {
      this.$store.commit('setRecurringSubtype', value);
    },
    everyX(value) {
      this.$store.commit('setRecurringEveryX', value);
    },
  },
  mounted() {
    this.$store.commit('initRecurringDays');
  },
};
</script>
